import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from './cn';

/**
 * Props for the Card root component.
 */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Card root container with rounded border and shadow.
 * Use together with CardHeader, CardContent and CardFooter.
 *
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Title</CardTitle>
 *     <CardDescription>Description</CardDescription>
 *   </CardHeader>
 *   <CardContent>Content goes here</CardContent>
 *   <CardFooter>Footer actions</CardFooter>
 * </Card>
 * ```
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('rounded-lg border border-ink/10 bg-card shadow-sm', className)}
      {...props}
    />
  );
});

/**
 * Props for the CardHeader component.
 */
export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Header section of a Card. Typically contains CardTitle and CardDescription.
 */
export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
  { className, ...props },
  ref,
) {
  return <div ref={ref} className={cn('flex flex-col gap-1.5 p-6', className)} {...props} />;
});

/**
 * Props for the CardTitle component.
 */
export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Title element inside a CardHeader. Renders as h3.
 */
export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(function CardTitle(
  { className, ...props },
  ref,
) {
  return (
    <h3
      ref={ref}
      className={cn('text-lg font-semibold leading-none tracking-tight text-ink', className)}
      {...props}
    />
  );
});

/**
 * Props for the CardDescription component.
 */
export interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Descriptive text inside a CardHeader. Renders as p.
 */
export const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  function CardDescription({ className, ...props }, ref) {
    return <p ref={ref} className={cn('text-sm text-ink-muted', className)} {...props} />;
  },
);

/**
 * Props for the CardContent component.
 */
export interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Main content area of a Card.
 */
export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(function CardContent(
  { className, ...props },
  ref,
) {
  return <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />;
});

/**
 * Props for the CardFooter component.
 */
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  /** Additional CSS classes. */
  className?: string;
}

/**
 * Footer section of a Card. Typically contains action buttons.
 */
export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(function CardFooter(
  { className, ...props },
  ref,
) {
  return <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />;
});
